// src/utils/reportePagosGenerator.js
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
// 👈 NUEVO: Importar función que obtiene los datos de la empresa actual
import { obtenerDatosEmpresa } from './tenantConfig';

// Configuración por defecto (fallback)
const JEAN_INFORMATICO_CONFIG = {
  nombre: 'JEAN INFORMATICO, C.A.',
  rif: 'J-41023119-0',
  slogan: 'Soluciones Tecnológicas',
  direccion: 'CALLE 30, CON AV. 33, C.C LATIN CENTER, LOCAL 01',
  ciudad: 'ACARIGUA, EDO. PORTUGUESA',
  telefono: '04145146450 / 5516151 / 02556223297',
  email: 'JEANINFORMATICOCA@GMAIL.COM',
  banco: 'BANCO MERCANTIL',
  cuenta: '0105 0115 6711 1523 2630'
};

/**
 * 👈 NUEVO: Obtiene la config de la empresa actual con fallback
 */
const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa();
    return {
      nombre: datos.nombreComercial || datos.nombre || JEAN_INFORMATICO_CONFIG.nombre,
      rif: datos.rif || JEAN_INFORMATICO_CONFIG.rif,
      slogan: datos.slogan || JEAN_INFORMATICO_CONFIG.slogan,
      direccion: datos.direccion || JEAN_INFORMATICO_CONFIG.direccion,
      ciudad: datos.ciudad || JEAN_INFORMATICO_CONFIG.ciudad,
      telefono: datos.telefono1
        ? `${datos.telefono1}${datos.telefono2 ? ' / ' + datos.telefono2 : ''}`
        : JEAN_INFORMATICO_CONFIG.telefono,
      email: datos.email || JEAN_INFORMATICO_CONFIG.email,
      banco: datos.banco || JEAN_INFORMATICO_CONFIG.banco,
      cuenta: datos.cuenta || JEAN_INFORMATICO_CONFIG.cuenta,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    console.warn('⚠️ No se pudo obtener config del tenant, usando fallback:', err);
    return { ...JEAN_INFORMATICO_CONFIG, logoBase64: null };
  }
};

let LOGO_BASE64 = '';
let TASA_ACTUAL = 0;

// ==================== CARGA DE LOGO ====================
/**
 * 👈 MODIFICADO: Prioriza el logo del tenant; si no hay, usa /logo-jean.jpg
 */
export const cargarLogo = async () => {
  try {
    // Primero intentar con el logo del tenant
    const empresa = getEmpresa();
    if (empresa.logoBase64) {
      const base64Limpio = empresa.logoBase64.includes(',')
        ? empresa.logoBase64.split(',')[1]
        : empresa.logoBase64;
      LOGO_BASE64 = base64Limpio;
      console.log('✅ Logo del tenant cargado');
      return LOGO_BASE64;
    }

    // Fallback al logo por defecto
    const response = await fetch('/logo-jean.jpg');
    if (!response.ok) throw new Error('Logo no encontrado');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        LOGO_BASE64 = reader.result.split(',')[1];
        resolve(LOGO_BASE64);
      };
      reader.onerror = () => {
        LOGO_BASE64 = '';
        resolve('');
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error cargando el logo:', error);
    LOGO_BASE64 = '';
    return '';
  }
};

export const getLogoBase64 = () => LOGO_BASE64;

// ==================== OBTENER TASA ACTUAL ====================
export const obtenerTasaActual = async () => {
  try {
    const result = await obtenerTasaVentaActual();
    if (result.success && result.data) {
      TASA_ACTUAL = result.data.precio_usd_bs || result.data.precioUsdBs || 52.00;
      console.log('💰 Tasa actual cargada:', TASA_ACTUAL);
    } else {
      TASA_ACTUAL = 52.00;
    }
    return TASA_ACTUAL;
  } catch (error) {
    console.error('Error obteniendo tasa:', error);
    TASA_ACTUAL = 52.00;
    return TASA_ACTUAL;
  }
};

// ==================== UTILIDADES ====================
export const formatearFechaCorta = (fechaStr) => {
  if (!fechaStr) return '—';
  try {
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return fechaStr;
  }
};

export const formatearFechaLarga = (fechaStr) => {
  if (!fechaStr) return '—';
  try {
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-VE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return fechaStr;
  }
};

export const formatearNumero = (valor, decimales = 2) => {
  if (valor === null || valor === undefined || isNaN(valor)) return '0,00';
  return Number(valor).toFixed(decimales).replace('.', ',');
};

export const formatearMonedaUSD = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return '$ 0,00';
  return `$ ${formatearNumero(valor)}`;
};

export const formatearMonedaBs = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return 'Bs 0,00';
  return `Bs ${formatearNumero(valor)}`;
};

// ==================== GENERAR HTML REPORTE DE PAGOS ====================
export const generarHTMLReportePagos = (pagos, filtros, resumen, tasa) => {
  if (!pagos || pagos.length === 0) {
    return `<div style="padding:40px;text-align:center;font-family:Arial;">No hay pagos para mostrar</div>`;
  }

  const empresa = getEmpresa();   // 👈 NUEVO: datos dinámicos del tenant
  const logoImg = getLogoBase64() ? `data:image/jpeg;base64,${getLogoBase64()}` : '';
  const tasaUsd = tasa || 52.00;

  // Determinar título según vista
  let titulo = 'REPORTE DE PAGOS';
  let subtitulo = '';
  
  if (filtros.vista === 'diario') {
    titulo = 'REPORTE DE PAGOS DEL DÍA';
    const fecha = filtros.fecha ? new Date(filtros.fecha) : new Date();
    subtitulo = fecha.toLocaleDateString('es-VE', { day: 'numeric', month: 'long', year: 'numeric' });
  } else if (filtros.vista === 'mensual' && filtros.mes) {
    titulo = 'REPORTE DE PAGOS DEL MES';
    const [anio, mes] = filtros.mes.split('-');
    const fecha = new Date(anio, mes - 1);
    subtitulo = fecha.toLocaleDateString('es-VE', { month: 'long', year: 'numeric' });
  } else {
    titulo = 'REPORTE DE PAGOS';
  }

  // Agrupar por método de pago
  const pagosPorMetodo = {};
  pagos.forEach(pago => {
    let metodo = pago.metodoPago || 'Otros';
    metodo = metodo.replace(/[^\w\sáéíóúÁÉÍÓÚñÑ]/g, '').trim();
    if (!metodo) metodo = 'Otros';
    if (!pagosPorMetodo[metodo]) {
      pagosPorMetodo[metodo] = {
        count: 0,
        totalUsd: 0,
        totalBs: 0
      };
    }
    pagosPorMetodo[metodo].count++;
    pagosPorMetodo[metodo].totalUsd += Number(pago.montoUsd) || 0;
    pagosPorMetodo[metodo].totalBs += Number(pago.montoBs) || 0;
  });

  // Preparar datos de resumen
  const totalPagos = pagos.length;
  const totalUsd = pagos.reduce((sum, p) => sum + (Number(p.montoUsd) || 0), 0);
  const totalBs = pagos.reduce((sum, p) => sum + (Number(p.montoBs) || 0), 0);

  // Construir filtros aplicados
  let filtrosAplicados = [];
  if (filtros.numeroOrden) filtrosAplicados.push(`Orden: ${filtros.numeroOrden}`);
  if (filtros.cliente) filtrosAplicados.push(`Cliente: ${filtros.cliente}`);
  if (filtros.metodoPago) filtrosAplicados.push(`Método: ${filtros.metodoPago}`);
  const textoFiltros = filtrosAplicados.length > 0 ? filtrosAplicados.join(' | ') : 'Ninguno';

  return `
    <div id="reporte-pagos-pdf" style="width:700px;margin:0 auto;padding:20px;font-family:'Courier New',monospace;font-size:10px;color:#000;background:white;">
      
      <!-- HEADER -->
      <div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:10px;margin-bottom:8px;">
        <div style="display:flex;align-items:center;width:80px;">
          ${logoImg ? `<img src="${logoImg}" style="height:60px;width:auto;max-width:80px;object-fit:contain;" onerror="this.style.display='none'">` : ''}
        </div>
        <div style="text-align:center;flex:1;">
          <div style="font-size:16px;font-weight:bold;">${empresa.nombre}</div>
          <div style="font-size:9px;">${empresa.direccion}</div>
          <div style="font-size:9px;">${empresa.ciudad}</div>
          <div style="font-size:9px;">TELFS: ${empresa.telefono}</div>
          <div style="font-size:8px;color:#555;">${empresa.email}</div>
          <div style="font-size:9px;font-weight:bold;margin-top:2px;">RIF: ${empresa.rif}</div>
        </div>
        <div style="display:flex;align-items:center;width:80px;">
          ${logoImg ? `<img src="${logoImg}" style="height:60px;width:auto;max-width:80px;object-fit:contain;" onerror="this.style.display='none'">` : ''}
        </div>
      </div>

      <div style="border-bottom:2px solid #000;margin-bottom:10px;"></div>

      <!-- TÍTULO -->
      <div style="text-align:center;padding:6px 0;margin-bottom:8px;">
        <div style="font-size:18px;font-weight:bold;letter-spacing:2px;">${titulo}</div>
        ${subtitulo ? `<div style="font-size:12px;margin-top:2px;font-weight:bold;">${subtitulo}</div>` : ''}
      </div>

      <!-- FILTROS -->
      <div style="padding:4px 0;margin-bottom:8px;font-size:9px;">
        <div><span style="font-weight:bold;">Filtros aplicados:</span> ${textoFiltros}</div>
      </div>

      <!-- RESUMEN -->
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;padding:8px;margin-bottom:10px;background:#f5f5f5;border:1px solid #000;">
        <div style="text-align:center;">
          <div style="font-size:8px;color:#000;text-transform:uppercase;letter-spacing:1px;">Total Pagos</div>
          <div style="font-size:18px;font-weight:bold;">${totalPagos}</div>
        </div>
        <div style="text-align:center;">
          <div style="font-size:8px;color:#000;text-transform:uppercase;letter-spacing:1px;">Total US$</div>
          <div style="font-size:18px;font-weight:bold;">${formatearMonedaUSD(totalUsd)}</div>
        </div>
        <div style="text-align:center;">
          <div style="font-size:8px;color:#000;text-transform:uppercase;letter-spacing:1px;">Total Bs</div>
          <div style="font-size:18px;font-weight:bold;">${formatearMonedaBs(totalBs)}</div>
        </div>
      </div>

      <!-- TABLA DE PAGOS - SIN LÍNEAS VERTICALES -->
      <div style="margin-bottom:10px;">
        <table style="width:100%;border-collapse:collapse;font-size:9px;">
          <thead>
            <tr style="border-bottom:2px solid #000;background:#e8e8e8;">
              <th style="padding:5px 6px;text-align:center;font-weight:bold;">Orden</th>
              <th style="padding:5px 6px;text-align:left;font-weight:bold;">Cliente</th>
              <th style="padding:5px 6px;text-align:center;font-weight:bold;">Fecha</th>
              <th style="padding:5px 6px;text-align:right;font-weight:bold;">US$</th>
              <th style="padding:5px 6px;text-align:right;font-weight:bold;">Bs</th>
              <th style="padding:5px 6px;text-align:center;font-weight:bold;">Método</th>
              <th style="padding:5px 6px;text-align:center;font-weight:bold;">Tipo Crédito</th>
              <th style="padding:5px 6px;text-align:center;font-weight:bold;">Referencia</th>
            </tr>
          </thead>
          <tbody>
            ${pagos.map((pago, index) => {
              let metodo = pago.metodoPago || '—';
              metodo = metodo.replace(/[^\w\sáéíóúÁÉÍÓÚñÑ]/g, '').trim();
              if (!metodo) metodo = '—';
              
              return `
                <tr style="border-bottom:1px solid #ddd;${index % 2 === 0 ? 'background:#fafafa;' : ''}">
                  <td style="padding:4px 6px;text-align:center;">${pago.numeroOrden || '—'}</td>
                  <td style="padding:4px 6px;">${pago.nombreCliente || 'Sin cliente'}</td>
                  <td style="padding:4px 6px;text-align:center;">${formatearFechaCorta(pago.fechaPago)}</td>
                  <td style="padding:4px 6px;text-align:right;font-weight:bold;">${formatearMonedaUSD(pago.montoUsd)}</td>
                  <td style="padding:4px 6px;text-align:right;">${formatearMonedaBs(pago.montoBs)}</td>
                  <td style="padding:4px 6px;text-align:center;">${metodo}</td>
                  <td style="padding:4px 6px;text-align:center;font-size:8px;">${pago.tipoCredito || 'N/A'}</td>
                  <td style="padding:4px 6px;text-align:center;font-size:8px;">${pago.referencia || 'S/R'}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>

      <!-- RESUMEN POR MÉTODO DE PAGO -->
      <div style="padding:8px;margin-bottom:8px;border:1px solid #000;background:#f9f9f9;">
        <div style="font-size:10px;font-weight:bold;margin-bottom:6px;text-transform:uppercase;letter-spacing:1px;border-bottom:1px solid #000;padding-bottom:4px;">Resumen por Método de Pago</div>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:6px;">
          ${Object.entries(pagosPorMetodo).map(([metodo, data]) => `
            <div style="font-size:8px;padding:4px 8px;border:1px solid #ccc;background:white;">
              <div style="font-weight:bold;font-size:9px;">${metodo}</div>
              <div style="display:flex;justify-content:space-between;margin-top:2px;">
                <span>Pagos:</span>
                <span style="font-weight:bold;">${data.count}</span>
              </div>
              <div style="display:flex;justify-content:space-between;">
                <span>US$:</span>
                <span style="font-weight:bold;">${formatearMonedaUSD(data.totalUsd)}</span>
              </div>
              <div style="display:flex;justify-content:space-between;">
                <span>Bs:</span>
                <span style="font-weight:bold;">${formatearMonedaBs(data.totalBs)}</span>
              </div>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- PIE -->
      <div style="text-align:center;font-size:7px;color:#666;margin-top:8px;padding-top:6px;border-top:1px solid #000;">
        ${empresa.nombre} - ${empresa.direccion} - ${empresa.ciudad}
        <br>
        Reporte generado el ${formatearFechaLarga(new Date())} - Tasa: ${formatearNumero(tasaUsd)} Bs/USD
      </div>
    </div>
  `;
};

// ==================== FUNCIÓN PRINCIPAL - DESCARGAR PDF ====================
export const descargarReportePagosPDF = async (pagos, filtros = {}) => {
  if (!pagos || pagos.length === 0) {
    alert('No hay pagos para generar el reporte');
    return false;
  }

  try {
    // Resetear logo para que se recargue el del tenant actual
    LOGO_BASE64 = '';
    await cargarLogo();

    const tasa = await obtenerTasaActual();

    const resumen = {
      totalPagos: pagos.length,
      totalUsd: pagos.reduce((sum, p) => sum + (Number(p.montoUsd) || 0), 0),
      totalBs: pagos.reduce((sum, p) => sum + (Number(p.montoBs) || 0), 0)
    };

    let nombreArchivo = 'REPORTE_PAGOS';
    if (filtros.vista === 'diario' && filtros.fecha) {
      const fecha = new Date(filtros.fecha);
      nombreArchivo += `_${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`;
    } else if (filtros.vista === 'mensual' && filtros.mes) {
      nombreArchivo += `_${filtros.mes}`;
    } else {
      nombreArchivo += `_${new Date().toISOString().split('T')[0]}`;
    }
    nombreArchivo += '.pdf';

    const htmlContent = generarHTMLReportePagos(pagos, filtros, resumen, tasa);

    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    container.style.cssText = 'position:absolute;left:-9999px;top:0;width:740px;background:white;padding:0;z-index:-1;';
    document.body.appendChild(container);

    await new Promise(resolve => setTimeout(resolve, 500));

    const canvas = await html2canvas(container, {
      scale: 2.5,
      useCORS: true,
      width: 740,
      height: container.scrollHeight,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = pdfHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
    heightLeft -= pdf.internal.pageSize.getHeight();

    while (heightLeft > 0) {
      position = heightLeft - pdfHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();
    }

    pdf.save(nombreArchivo);
    document.body.removeChild(container);

    return true;
  } catch (error) {
    console.error('Error generando PDF:', error);
    alert('Error al generar el PDF: ' + error.message);
    return false;
  }
};

// ==================== EXPORTACIÓN ====================
export default {
  JEAN_INFORMATICO_CONFIG,
  cargarLogo,
  getLogoBase64,
  obtenerTasaActual,
  formatearFechaCorta,
  formatearFechaLarga,
  formatearNumero,
  formatearMonedaUSD,
  formatearMonedaBs,
  generarHTMLReportePagos,
  descargarReportePagosPDF
};